import ReactECharts from "echarts-for-react";
import { useEffect, useRef, useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";
import ComboFilters from "../../ComboFilters";
import {
  getGrowthPercentage,
  getLastSixYears,
  months,
  Trend,
} from "../../Reports";

import dayjs from "dayjs";
import { ReportsAPI } from "providers/ReportsAPI";
import { If } from "shared/components/control-statements";
import { Loading } from "@components";

export const LifeCycleStagesChart = ({
  selectedData,
  onClose,
  startOn2025,
  onFetchData,
  loading,
}) => {
  const currentYear = new Date().getFullYear();

  const [data, setData] = useState({
    novos: [],
    onboarding: [],
    engajados: [],
    atrasados: [],
  });

  const [selectedYear, setSelectedYear] = useState({
    name: currentYear,
    id: currentYear,
  });

  const formatData = () => {
    const today = dayjs();

    const result = {};

    for (const key in data) {
      result[key] = data[key].map((item) =>
        !item || item?.value === 0 ? null : item
      );
    }

    if (selectedYear.id < today.year()) return result;

    const isCurrentYear = selectedYear.id === today.year();

    let limit = null;

    if (isCurrentYear) limit = today.month();

    for (const key in result) {
      result[key] =
        limit !== null ? result[key].slice(0, limit + 1) : result[key];
    }

    return result;
  };

  const series = formatData();

  const roundValue = (v) => {
    return Math.round(v / 10) * 10;
  };

  const getVisibleSeriesMin = (selected) => {
    let min = Infinity;
    let max = -Infinity;

    const normalizeLabel = (label) =>
      label
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, "_");

    for (const label in selected) {
      if (!selected[label]) continue;

      const key = normalizeLabel(label);
      const serieData = data[key];
      if (!serieData) continue;

      const values = serieData
        .map((d) => Number(d?.value))
        .filter((v) => !isNaN(v) && v !== 0);

      if (values.length === 0) continue;

      const localMin = Math.min(...values);
      const localMax = Math.max(...values);
      min = Math.min(min, localMin);
      max = Math.max(max, localMax);
    }

    if (!isFinite(min)) return 0;

    const padding = (max - min) * 0.2;
    const ajustado = Math.max(0, Math.floor(min - padding));
    const rounded = roundValue(ajustado);

    return rounded;
  };

  const handleLegendSelectChanged = (e) => {
    const instance = echartsRef.current.getEchartsInstance();
    const selected = e.selected;

    const newMin = getVisibleSeriesMin(selected);

    instance.setOption({
      yAxis: {
        startValue: newMin,
      },
    });
  };

  const buildOptions = () => {
    return {
      grid: {
        left: 10,
        right: 30,
        top: 30,
        bottom: 70,
        containLabel: true,
      },
      tooltip: {
        trigger: "item",
        axisPointer: {
          type: "none",
        },
        backgroundColor: "#2B2B2B",
        borderRadius: 8,
        padding: [8, 8],
        borderWidth: 0,
        textStyle: {
          color: "#FFF",
          fontSize: 10,
        },
        formatter: (params) => {
          const name = params.name;

          if (params.data === null || params.value == null) return;

          const formattedValue = params.value.toLocaleString("pt-BR");

          return `
            <div>
              <strong>${name}</strong><br/><br/>
              ${params.marker} ${params.seriesName}: <b>${formattedValue}</b>
            </div>
          `;
        },
      },
      legend: {
        type: "scroll",
        orient: "horizontal",
        pageIconColor: "#6D6DF2",
        pageIconInactiveColor: "#4C4C4C",
        backgroundColor: "#1C1C1C",
        borderRadius: 10,
        padding: 20,
        pageIconSize: 10,
        pageTextStyle: {
          color: "#B3B3B3",
          fontSize: 10,
          fontWeight: 800,
        },
        pageIcons: {
          horizontal: [
            "path://M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z",
            "path://M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z",
          ],
        },
        top: "bottom",
        icon: "circle",
        itemWidth: 8,
        itemHeight: 8,
        itemGap: 10,
        textStyle: {
          color: "#808080",
          fontSize: 12,
        },
        inactiveColor: "#555",
      },
      xAxis: {
        type: "category",
        data: months,
        axisLine: {
          show: true,
          lineStyle: {
            color: "rgba(217, 217, 217, 0.5)",
          },
        },
        splitLine: {
          lineStyle: {
            color: "rgba(51, 51, 51, 0.55)",
            width: 1,
            type: "solid",
          },
        },
      },
      yAxis: {
        type: "value",
        axisLine: {
          show: true,
          lineStyle: {
            color: "rgba(217, 217, 217, 0.5)",
          },
        },
        axisLabel: {
          formatter: (value) => `${value.toLocaleString("pt-BR")}`,
        },
        splitLine: {
          lineStyle: {
            color: "rgba(51, 51, 51, 0.55)",
            width: 0,
            type: "solid",
          },
        },
      },
      series: [
        {
          name: "Novos",
          type: "line",
          data: series.novos,
          color: "#e1ffb4",
          symbolSize: 8,
          itemStyle: {
            color: "#e1ffb4",
            borderColor: "#e1ffb4",
            borderWidth: 2,
          },
          lineStyle: {
            width: 2,
            color: "#e1ffb4",
          },
          symbol: "circle",
          connectNulls: false,
        },
        {
          name: "Onboarding",
          type: "line",
          data: series.onboarding,
          color: "#6af3ee",
          symbolSize: 8,
          itemStyle: {
            color: "#6af3ee",
            borderColor: "#6af3ee",
            borderWidth: 2,
          },
          lineStyle: {
            width: 2,
            color: "#6af3ee",
          },
          symbol: "circle",
          connectNulls: false,
        },
        {
          name: "Engajados",
          type: "line",
          data: series.engajados,
          color: "#a67666",
          symbolSize: 8,
          itemStyle: {
            color: "#a67666",
            borderColor: "#a67666",
            borderWidth: 2,
          },
          lineStyle: {
            width: 2,
            color: "#a67666",
          },
          symbol: "circle",
          connectNulls: false,
        },
        {
          name: "Atrasados",
          type: "line",
          data: series.atrasados,
          color: "#E48186",
          symbolSize: 8,
          itemStyle: {
            color: "#E48186",
            borderColor: "#E48186",
            borderWidth: 2,
          },
          lineStyle: {
            width: 2,
            color: "#E48186",
          },
          symbol: "circle",
          connectNulls: false,
        },
      ],
    };
  };

  const [options, setOptions] = useState(buildOptions());
  const [loadingHistoric, setLoadingHistoric] = useState(true);

  const echartsRef = useRef(null);

  useEffect(() => {
    const chart = echartsRef.current?.getEchartsInstance();
    if (chart) {
      setTimeout(() => chart.resize(), 0);
    }
  }, []);

  const fetchData = async () => {
    const payload = { year: selectedYear?.id };

    const results = await onFetchData(payload);

    const series = {
      novos: [],
      onboarding: [],
      engajados: [],
      atrasados: [],
    };

    results.forEach((result) => {
      for (const key in series) {
        if (result && result[key] !== undefined && result[key] !== null) {
          series[key].push(result[key]);
        } else {
          series[key].push(null);
        }
      }
    });

    setData(series);
  };

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  useEffect(() => {
    setOptions(buildOptions());
  }, [data]);

  return (
    <div className={styles.monthYearLegendChartContainer}>
      <div className={styles.monthYearHeader}>
        <p className={styles.title}>{selectedData?.name}</p>
        <div className="d-flex align-center">
          <div className={styles.close} onClick={onClose}>
            <i className="fas fa-times" />
          </div>
        </div>
      </div>

      <div className={styles.monthYearLegendContent}>
        <If cond={loading}>
          <div className={styles.loading}>
            <Loading />
          </div>
        </If>

        <If cond={!loading}>
          <div className={styles.legendContainer}>
            <div className={styles.header}>Crescimento no ano</div>
            <div className={styles.legend}>
              <div className="d-flex align-center">
                <div className={styles.dot} style={{ background: "#e1ffb4" }} />
                <p className={styles.name}>Clientes novos</p>
              </div>
              <Trend
                growth={getGrowthPercentage({
                  data: data.novos,
                  selectedFilter: "year",
                  selectedYear,
                })}
              />
            </div>
            <div className={styles.legend}>
              <div className="d-flex align-center">
                <div className={styles.dot} style={{ background: "#6af3ee" }} />
                <p className={styles.name}>Clientes em onboarding</p>
              </div>
              <Trend
                growth={getGrowthPercentage({
                  data: data.onboarding,
                  selectedFilter: "year",
                  selectedYear,
                })}
              />
            </div>
            <div className={styles.legend}>
              <div className="d-flex align-center">
                <div className={styles.dot} style={{ background: "#a67666" }} />
                <p className={styles.name}>Clientes engajados</p>
              </div>
              <Trend
                growth={getGrowthPercentage({
                  data: data.engajados,
                  selectedFilter: "year",
                  selectedYear,
                })}
              />
            </div>
            <div className={styles.legend}>
              <div className="d-flex align-center">
                <div className={styles.dot} style={{ background: "#E48186" }} />
                <p className={styles.name}>Clientes atrasados</p>
              </div>
              <Trend
                growth={getGrowthPercentage({
                  data: data.atrasados,
                  selectedFilter: "year",
                  selectedYear,
                })}
                invertColorSign
              />
            </div>
          </div>

          <div className={styles.chartContainer}>
            <div className={styles.filters}>
              <div className="d-flex align-center w-100 justify-between">
                <div />
                <ComboFilters
                  options={getLastSixYears(startOn2025)}
                  onSelect={(option) => setSelectedYear(option)}
                  selectedOption={selectedYear}
                />
              </div>
            </div>
            <div className={styles.monthYearChart}>
              <ReactECharts
                ref={echartsRef}
                option={options}
                style={{ width: "100%", height: "100%" }}
                notMerge={true}
                lazyUpdate={true}
                opts={{ renderer: "canvas" }}
                onEvents={{
                  legendselectchanged: handleLegendSelectChanged,
                  // mouseover: handleClick,
                }}
              />
            </div>
          </div>
        </If>
      </div>
    </div>
  );
};
